<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{$t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation}}
          </h6>

          <div class="element-box mt-4">
            <div class="row mt-4">
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>
              <CoachingButton
                  :enabled="companySettings?.caseManagementCoachingEnabled"
                  route-name="caseManagementCoachingNewAndActiveList"
                  coaching-type="caseManagement"
                  label="DRV Fallmanagement"/>
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <div>
            <h6 class="mt-5">{{ $t('dashboard.customer_information') }}</h6>
            <div class="row mt-4">
              <div class="col-lg-3 col-md-6 col-sm-3">
                <CardInfo :label="$t('general.active_customers')" :value="dashboardData?.totalActiveCoaching"/>
              </div>
              <div class="col-lg-3 col-md-6 col-sm-3">
                <CardInfo :label="$t('general.new_customers')" :value="dashboardData?.totalNewCoaching"/>
              </div>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6 class="mt-3">
              Einheiten im Jahr {{ filters?.year }} fertiggestellt
            </h6>

            <div class="row mt-4">
              <div class="col-lg-3 col-md-4 col-sm-12">
                <SelectVue
                    v-model="filters.coachingType"
                    :options="dropdownCoachingType"
                    :is-clear="false"
                />
              </div>
              <div class="col-lg-2 col-md-4 col-sm-12">
                <SelectVue
                    v-model="filters.year"
                    :options="dropdownYears"
                    :is-clear="false"
                />
              </div>

              <div class="col-auto text-center">
                <button class="btn btn-primary custom_search_button2" @click="getChartBarArea">
                  {{ $t('general.search') }}
                </button>
              </div>
            </div>
            <VueApexCharts width="100%" type="bar" :options="chartOptionsBar" :series="seriesSignBar"/>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6 class="mt-3">
              Arbeitsstunden und geleistete Einheiten im Jahr {{ filters?.year }}
            </h6>
            <div class="row mt-4">
              <div class="col-sm-12">
                <VueApexCharts width="100%" type="area" :options="chartOptionsArea" :series="seriesWorksArea"/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar Infos
          </h6>
        </template>

        <div class="element-box pie">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts :options="chartOptions" :series="seriesActiveCoach"/>
        </div>

        <div class="element-box pie">
          <h6>{{ $t('general.new_customers') }}</h6>
          <VueApexCharts :options="chartOptionsNewCoach" :series="seriesNewCoach"/>
        </div>

        <!-- <div class="element-box">
            <h6 class="mb-3">heutige Kalendereinträge</h6>
        </div>
        
        <div class="element-box">
            <h6 class="mb-3">bevorstehender Urlaub</h6>
        </div> -->
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardCoachService from '@/api/Dashboard/Coach';
import CardInfo from '@/components/card/CardInfo.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast } from '@/components/VueToast';
import { getCoachingTypes, getYears } from '@/shared/dropdowns';
import { optionsChartArea, optionsChartBar, optionsChartDonut } from '@/shared/utils/apex';
import { MonthsUtils } from '@/shared/utils/MonthsUtils';
import { piniaCompanySettingsStore } from '@/store/piniaCompanySettingsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import VueApexCharts from 'vue3-apexcharts';
import {initAppIfAuthenticated} from "@/shared/functions";

const { t }                = useI18n();
const dropdownYears        = ref([]);
const dropdownCoachingType = ref([]);

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const dashboardData = ref({});

const seriesActiveCoach = ref([]);
const seriesNewCoach    = ref([]);
const seriesWorksArea   = ref([]);
const seriesSignBar     = ref([]);

const chartOptions         = ref(optionsChartDonut);
const chartOptionsNewCoach = ref(optionsChartDonut);

const chartOptionsBar = ref({
  ...optionsChartBar,
  xaxis: {
    categories: MonthsUtils(t),
  },
  colors: [
    'rgba(67, 173, 78, 0.6)',
    'rgba(75, 192, 192, 0.6)',
  ],
  plotOptions: {
    bar: {
      distributed: false,
    },
  },
});

const chartOptionsArea = ref({
  ...optionsChartArea,
  xaxis: {
    categories: MonthsUtils(t),
  },
  colors: [
    'rgba(67, 173, 78, 0.6)',
    'rgba(75, 192, 192, 0.6)',
  ],
});

const filters = ref({
  coachingType: 'private_coaching',
  year: new Date().getFullYear(),
});

async function getDashboardData() {
  try {
    const { data } = await dashboardCoachService.getDashboardData();

    dashboardData.value     = data.data;
    seriesActiveCoach.value = Object.values(data.data.countActiveCoaching);
    seriesNewCoach.value    = Object.values(data.data.countNewCoaching);

    chartOptions.value         = {
      ...chartOptions.value,
      labels: [
        `Einzelcoaching ${data.data.countActiveCoaching.privateCoaching}`,
        `Soziotherapie ${data.data.countActiveCoaching.socioTherapyCoaching}`,
        `Betreutes Wohnen ${data.data.countActiveCoaching.assistedLivingCoaching}`,
        `Gruppenmaßnahmen ${data.data.countActiveCoaching.groupCoaching}`,
        `DRV Fallmanagement ${data.data.countActiveCoaching.caseManagementCoaching}`,
        `Private Weiterbildung ${data.data.countActiveCoaching.privateLearningCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.totalActiveCoaching.toString(),
              },
            },
          },
        },
      },
    };
    chartOptionsNewCoach.value = {
      ...chartOptions.value,
      labels: [
        `Einzelcoaching ${data.data.countNewCoaching.privateCoaching}`,
        `Soziotherapie ${data.data.countNewCoaching.socioTherapyCoaching}`,
        `Betreutes Wohnen ${data.data.countNewCoaching.assistedLivingCoaching}`,
        `Gruppenmaßnahmen ${data.data.countNewCoaching.groupCoaching}`,
        `DRV Fallmanagement ${data.data.countNewCoaching.caseManagementCoaching}`,
        `Private Weiterbildung ${data.data.countNewCoaching.privateLearningCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.totalNewCoaching.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

function getDropdowns() {
  getYears(dropdownYears);
  getCoachingTypes(dropdownCoachingType);
}

async function getChartBarArea() {
  try {
    const { data }        = await dashboardCoachService.getUnitsDoneChart(filters.value);
    seriesSignBar.value   = [ data.data.chartData.signedUnits, data.data.chartData.unsignedUnits ];
    seriesWorksArea.value = [ data.data.chartData.totalUnits, data.data.chartData.sumHoursWorked ];
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted( async() => {
  await initAppIfAuthenticated()
  await getDashboardData();
  await getChartBarArea();
  getDropdowns();
});

</script>

<style scoped>

::v-deep(.pie .apexcharts-legend) {
  display: block !important;
}

</style>